<template>
  <GBox class="login-form-wrapper">
    <form
      @submit.prevent="onSubmit"
    >
      <GText
        variant="headline-03"
        class="sc-bold"
      >
        Giriş Yap
      </GText>
      <GBox
        :mt="20"
        :mb="20"
      >
        <GInput
          v-model.trim="formData.value"
          is-outline-label
          label="E-Posta / Telefon"
          type="text"
          :error="error.value.show || !!loginError"
          :feedback="error.value.show?error.value.message:''"
          :placeholder="$t('login.emailOrPhone')"
          class="email-phone"
        />
      </GBox>
      <GBox
        :mb="20"
        class="password-input-wrapper"
      >
        <GInput
          v-model.trim="formData.password"
          is-outline-label
          :type="passwordType"
          label="Şifreniz"
          class="password"
          :error="error.password.show || !!loginError"
          :feedback="error.password.show ? error.password.message: ''"
          :placeholder="$t('login.password')"
        />
        <GIcon
          :name="passwordIcon"
          stroke="var(--dark-grey-500)"
          class="password-icon"
          @click="togglePasswordVisibility"
        />
      </GBox>
      <GBox
        flex
        justify="end"
        :mb="20"
      >
        <RouterLink
          :to="{ name: 'Forgot' }"
          class="sc-link forgot-password"
        >
          <GText color="orange-500">
            {{ $t('login.messages.onlyForgot') }}
          </GText>
        </RouterLink>
      </GBox>
      <GBox
        class="error-message"
        :mb="10"
        v-if="loginError"
      >
        <GText color="red-500">
          {{ loginError }}
        </GText>
      </GBox>

      <InvisibleRecaptcha
        :disabled="isFromAdminPanel"
        @callback="onCaptcha"
        :validate="onValidate"
        class="btn btn-lg btn-mp-primary btn-block"
        type="submit"
        :site-key="siteKey"
      >
        {{ submitButtonText }}
      </InvisibleRecaptcha>
      <GBox
        flex
        class="g-d-justify-center g-d-align-center g-mt-30 application-form-info"
      >
        <GText
          inline
          color="mid-grey-500"
        >
          Satıcı değil misiniz?
        </GText>
        <a href="/onboarding/satici-formu">
          <GText
            color="orange-500"
            class="g-ml-4 sc-link"
            inline
          >
            Şimdi Trendyol’da Satıcı Olun
          </GText>
        </a>
      </GBox>
      <MobileRedirectWidgets class="mobile-redirect-widget-container" />
    </form>
  </GBox>
</template>

<script>
import AuthService from '@/services/AuthService';
import CommonUtils from '@/utils/CommonUtils';
import GText from '@trendyol-js/grace/core/GText';
import GBox from '@trendyol-js/grace/core/GBox';
import GInput from '@trendyol-js/grace/core/GInput';
import GIcon from '@trendyol-js/grace/core/GIcon';
import MobileRedirectWidgets from '@/views/LoginNew/MobileRedirectWidgets';
import InvisibleRecaptcha from '@/components/Shared/InvisibleRecaptcha';

export default {
  name: 'LoginForm',

  components: {
    MobileRedirectWidgets,
    GText,
    GBox,
    GInput,
    InvisibleRecaptcha,
    GIcon,
  },

  data () {
    return {
      isFromAdminPanel: false,
      error: {
        value: {
          show: false,
          message: 'E-Posta veya Telefon numarası giriniz.',
        },
        password: {
          show: false,
          message: 'Şifrenizi giriniz.',
        },
      },
      formData: {
        value: '',
        password: '',
        gcResponse: '',
      },
      loginError: false,
      visiblePassword: false,
      recaptchaError: false,
    };
  },

  async created () {
    const hasTokenInQuery = 'token' in this.$route.query;

    if (AuthService.isAuthenticated() && !hasTokenInQuery) {
      window.location.href = '/';
    } else if (hasTokenInQuery && this.$route.query.token.length) {
      this.isFromAdminPanel = true;

      AuthService.logout();

      const params = {
        token: this.$route.query.token,
        refreshToken: this.$route.query.refreshToken,
        channels: this.$route.query.channels,
      };

      await AuthService.loginWithToken(params, true);

      window.location.href = '/';
    }
  },

  computed: {
    siteKey () {
      return process.env.VUE_APP_LOGIN_INVISIBLE_RECAPTCHA_SITE_KEY;
    },
    passwordType () {
      return this.visiblePassword ? 'text' : 'password';
    },
    passwordIcon () {
      return this.visiblePassword ? 'eye-off' : 'eye';
    },
    submitButtonText () {
      return this.isFromAdminPanel
        ? this.$t('login.waitLogin')
        : this.$t('login.login');
    },
  },

  methods: {
    onValidate () {
      this.error.value.show = !(CommonUtils.validateEmail(this.formData.value) || CommonUtils.validatePhone(this.formData.value));
      this.error.password.show = !this.formData.password;
      this.loginError = false;
      return !this.error.password.show && !this.error.value.show;
    },
    async onCaptcha (captchaPayload) {
      try {
        this.error.password.show = false;
        this.error.value.show = false;
        let payload = {
          value: this.formData.value,
          password: this.formData.password,
          gcResponse: captchaPayload,
        };
        const isPhoneNumber = CommonUtils.validatePhone(payload.value);
        if (isPhoneNumber) {
          payload.value = CommonUtils.formatPhone(payload.value);
        }
        const { token: authToken } = await AuthService.login(payload, {
          hideErrorMessage: true,
        });

        this.$gtm.trackEvent({
          event: 'GAEvent',
          eventCategory: 'Onboarding - SC Login',
          eventAction: isPhoneNumber
            ? 'Telefon Numarası İle Giriş'
            : 'Email İle Giriş',
          eventLabel: '-',
        });
        setTimeout(() => {
          const { phoneVerified } = window.scUtils.decodeJWT(authToken);
          const redirect = this.$route.query.redirect || '';

          if (phoneVerified === false && !redirect.startsWith('/account/sso')) {
            window.location.href = '/account/info?tab=UserInformation&phoneVerificationRequired=true';

            return true;
          }

          let redirectUrl = '/';

          if (redirect && redirect[0] === '/') {
            redirectUrl = redirect;
          }
          window.location.href = redirectUrl;
          window.history.replaceState({}, '', '/');
        }, 500);
      } catch (error) {
        this.loginError = error.response.data.errors[0].message;
      }
    },
    togglePasswordVisibility () {
      this.visiblePassword = !this.visiblePassword;
    },
    onSubmit () {
    },
  },
};
</script>

<style lang="scss" scoped>
.login-form-wrapper {
  padding: 32px;
  border-radius: 6px;
  background-color: var(--white);
  height: 100%;

  ::v-deep .g-field-wrapper {
    .content input {
      letter-spacing: normal !important;
    }
    .sub-label{
      position: absolute;
      bottom: -17px;
    }
  }

  .password-input-wrapper{
    position: relative;
    .password-icon {
      position: absolute;
      right: 11px;
      bottom: 8px;
      cursor: pointer;
    }
  }

  .text {
    letter-spacing: 1.4px !important;
  }

  @media only screen and (max-width:1250px) {
    padding: 15px;

   .application-form-info {
     flex-direction: column;
   }
  }

  ::v-deep .recaptcha iframe {
    border: 1px solid transparent;
    border-radius: 4px;
  }

  ::v-deep .recaptcha.is-invalid iframe {
    border-color: var(--red-500);;
  }
}

.mobile-redirect-widget-container{
  margin-top: 44px;
}
</style>
